import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuyspreadsheet.run";
const logoUrl = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Best Sourcing Spreadsheet Tools for Ecommerce Sellers",
  description: "A curated ranking of the top five spreadsheet-based sourcing systems used by dropshippers and online store owners. Compare features, pricing, and ideal use cases.",
  datePublished: "2026-07-01",
  dateModified: "2026-07-01",
  author: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    url: siteUrl,
  },
  publisher: {
    "@type": "Organization",
    name: "GTBuy Spreadsheet",
    logo: {
      "@type": "ImageObject",
      url: logoUrl,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${siteUrl}/best-sourcing-tools`,
  },
};

const toolsData = [
  { name: "GTBuy Spreadsheet", bestFor: "Scaling sellers (50-500+ products)", weakness: "Moderate learning curve", strength: "Unmatched structural depth" },
  { name: "Google Sheets Custom", bestFor: "Beginners and small catalogs", weakness: "No built-in sourcing logic", strength: "Free and familiar" },
  { name: "Airtable Sourcing DB", bestFor: "Visual data management", weakness: "Monthly subscription cost", strength: "Rich media attachments" },
  { name: "ERP Systems (NetSuite, etc.)", bestFor: "Enterprise operations", weakness: "Expensive and complex", strength: "Full business integration" },
  { name: "Notion Sourcing Tracker", bestFor: "Solo entrepreneurs", weakness: "Limited calculation power", strength: "Beautiful interface" },
];

export default function BestSourcingTools() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />
      <main>
        <section className="w-full pt-24 pb-12 md:pt-32 md:pb-16 px-6 md:px-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer flex items-center gap-1"><i className="ri-arrow-left-line"></i> Back to Home</Link>
              <span className="text-foreground-300">/</span>
              <span className="text-sm text-foreground-500">Guides</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">Guide &middot; 8 min read</span>
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-tight mb-4">Best Sourcing Spreadsheet Tools for Ecommerce Sellers</h1>
            <p className="text-base text-foreground-600 leading-relaxed max-w-2xl">A curated ranking of the top five spreadsheet-based sourcing systems used by dropshippers and online store owners. Compare features, pricing philosophy, and ideal use cases to find your perfect fit.</p>
            <p className="text-xs text-foreground-400 mt-4">Published: July 2026</p>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 pb-10">
          <div className="max-w-4xl mx-auto">
            <div className="aspect-[16/7] w-full rounded-2xl overflow-hidden bg-background-200">
              <img src="https://readdy.ai/api/search-image?query=Multiple%20devices%20laptop%20tablet%20and%20smartphone%20arranged%20on%20warm%20wooden%20desk%20all%20displaying%20different%20spreadsheet%20and%20sourcing%20tool%20interfaces%20with%20colorful%20data%20grids%20modern%20office%20with%20bookshelf%20and%20plant%20bright%20natural%20lighting%20professional%20photography%20high%20detail&width=1600&height=700&seq=article-best-tools-header&orientation=landscape"
                alt="Best Sourcing Spreadsheet Tools" title="Best Sourcing Spreadsheet Tools for Ecommerce" className="w-full h-full object-cover" loading="eager" />
            </div>
          </div>
        </section>

        <article className="w-full px-6 md:px-10 pb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-10 mb-4">Why Spreadsheet-Based Sourcing Matters</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">The right sourcing tool can mean the difference between profitable, scalable growth and chaotic, error-prone operations. Spreadsheet-based systems remain popular because they offer <strong>unmatched flexibility</strong> — you can customize every formula, every view, and every data relationship to match your exact business model.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-8">After evaluating the major options available to ecommerce sellers in 2026, here is our definitive ranking based on structural depth, ease of use, scalability, and overall value.</p>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">Top 5 Sourcing Tools Ranked</h2>
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tl-lg">Tool</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200">Best For</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200">Main Weakness</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-foreground-800 border-b border-background-200 rounded-tr-lg">Key Strength</th>
                  </tr>
                </thead>
                <tbody>
                  {toolsData.map((tool, i) => (
                    <tr key={i} className="hover:bg-background-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-foreground-700 font-semibold border-b border-background-200">{i + 1}. {tool.name}</td>
                      <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">{tool.bestFor}</td>
                      <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">{tool.weakness}</td>
                      <td className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200">{tool.strength}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mt-12 mb-4">The Bottom Line</h2>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">For most serious ecommerce sellers, the <strong>GTBuy Spreadsheet</strong> system hits the sweet spot between structural power and practical usability. It is more organized than a basic Google Sheet but far more affordable and flexible than a full ERP system.</p>
            <p className="text-base text-foreground-700 leading-relaxed mb-4">Start with free tools like Google Sheets to learn the basics, then graduate to GTBuy when your catalog outgrows manual tracking. Skip the middle-ground tools that lock you into subscriptions without delivering proportional value.</p>

            <p className="text-sm text-foreground-500 italic mb-4">
              Continue reading:{" "}
              <Link to="/what-is-gtbuy-spreadsheet" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">What is GTBuy Spreadsheet?</Link>{" "}&middot;{" "}
              <Link to="/gtbuy-spreadsheet-review" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">GTBuy Review 2026</Link>{" "}&middot;{" "}
              <Link to="/" className="text-primary-600 hover:text-primary-700 underline cursor-pointer">Homepage</Link>
            </p>
          </div>
        </article>

        <section className="relative w-full py-20 px-6 md:px-10 bg-foreground-950 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-background-50 mb-4">Find the Right Products to Source</h2>
            <p className="text-foreground-400 text-sm mb-6">Browse 14 curated fashion categories on KA Spreadsheet. Apply any of these sourcing tools to build your product pipeline.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">Shop KA Spreadsheet</a>
              <Link to="/" className="px-6 py-3 rounded-full bg-foreground-800 text-background-50 font-medium text-sm hover:bg-foreground-700 transition-colors cursor-pointer whitespace-nowrap">Back to Homepage</Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}